import { createSessionScenario } from '../../../shared/sessionScenario';
import { parseScenarioTriggers, parseScenarioWorkflow } from '../../services/scenarioPresentation';
import CustomAgentPicker from './CustomAgentPicker';
import RecentScenarioCards from './RecentScenarioCards';
import CampaignCard from '../account/CampaignCard';
import { sessionModelReference } from '../../utils/sessionModel';
import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, store } from '../../store';
import { clearCurrentSession, setCurrentSession, setSessions, setStreaming, addSession, addMessage, updateSessionStatus, setNewTaskPromptMode, setSelectedDesktopClient, setSelectedDesktopCwd } from '../../store/slices/coworkSlice';
import { setAvailableModels, setSelectedModel } from '../../store/slices/modelSlice';
import { clearActiveSkills, setActiveSkillIds } from '../../store/slices/skillSlice';
import { clearSelection } from '../../store/slices/quickActionSlice';
import { coworkService } from '../../services/cowork';
import { relayService, getTargetClient } from '../../services/relay';
import { findRelayModel, relayModelKey } from '../../services/relayModelKey';
import { skillService } from '../../services/skill';
import { i18nService } from '../../services/i18n';
import { scenarioService, type MyScenario } from '../../services/scenario';
import { canApplyScenarioToConversation } from '../../services/scenarioConversation';
import { resolveScenarioSkills } from '../../services/scenarioSkills';
import { authService } from '../../services/auth';
import CoworkPromptInput, { type CoworkAttachment, type CoworkPromptInputRef, buildInlinedSkillPrompt } from './CoworkPromptInput';
import CoworkSessionDetail from './CoworkSessionDetail';
import SidebarToggleIcon from '../icons/SidebarToggleIcon';
import ComposeIcon from '../icons/ComposeIcon';
import WindowTitleBar from '../window/WindowTitleBar';
import type { SettingsOpenOptions } from '../Settings';
import ConnectedToolsBar from './ConnectedToolsBar';
import CoworkPromptModeSwitch from './CoworkPromptModeSwitch';
import type { CoworkSession, SandboxProfile, SandboxImage } from '../../types/cowork';
import type { Skill } from '../../types/skill';
import type { AuthState } from '../../types/auth';
import ScenarioAppIcon from '../scenarios/ScenarioAppIcon';

const DESKTOP_ATTACHMENT_MAX_BYTES = 20 * 1024 * 1024;
const INPUT_FILE_LABEL = '输入文件';

async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}

function withAttachmentPaths(prompt: string, paths: string[]): string {
  const lines = paths.map(path => `${INPUT_FILE_LABEL}: ${path}`).join('\n');
  return prompt.trim() ? `${prompt.trim()}\n\n${lines}` : lines;
}

/** 组装场景会话的 system prompt；Relay 可排除身份段，由 Desktop 统一注入身份。 */
// 会话 systemPrompt 的基线是空串，场景 prompt 只从 activeScenarioRef 现算(见各调用点)。
// 不再读取全局 config.systemPrompt：它没有任何用户可编辑入口，唯一写入者曾是
// enterScenarioChat，结果进过一次场景后，之后每个普通"新建任务"都继承那个场景的人设
// (真实事故：新建"问候"会话，AI 自称"金融市场流动性分析师")。跨会话越界的根源即此。
function buildScenarioSystemPrompt(
  scenario: MyScenario,
  options: { includeIdentity?: boolean } = {},
): string {
  const parts: string[] = [];
  const includeIdentity = options.includeIdentity ?? true;

  if (scenario.system_prompt) {
    parts.push(scenario.system_prompt);
  }

  if (includeIdentity && scenario.expert_name) {
    parts.push(`# 专家身份\n你的身份是「${scenario.expert_name}」。`);
  }

  if (scenario.description) {
    parts.push(`# 场景描述\n${scenario.description}`);
  }

  try {
    const wfSteps = parseScenarioWorkflow(scenario.workflow_steps);
    if (wfSteps.length > 0) {
      const stepsText = wfSteps
        .map((s, idx) => `${idx + 1}. ${s.title}${s.description ? ' — ' + s.description : ''}`)
        .join('\n');
      parts.push(`# 操作流程\n请按照以下步骤引导用户完成任务：\n${stepsText}`);
    }
  } catch {}

  if (scenario.skills && scenario.skills.length > 0) {
    const skillNames = scenario.skills.map((s) => s.display_name || s.skill_name).join('、');
    parts.push(`# 关联技能\n你可以使用以下技能：${skillNames}`);
  }

  if (scenario.knowledge) {
    parts.push(`# Knowledge\n${scenario.knowledge}`);
  }

  return parts.join('\n\n');
}

export interface CoworkViewProps {
  onRequestAppSettings?: (options?: SettingsOpenOptions) => void;
  onShowSkills?: () => void;
  isSidebarCollapsed?: boolean;
  onToggleSidebar?: () => void;
  onNewChat?: () => void;
  authState?: AuthState | null;
  isMobile?: boolean;
  activeScenario?: MyScenario | null;
  onSelectScenario?: (scenario: MyScenario) => void;
  onShowScenarios?: () => void;
  /** 静默清除场景（不导航）—— 快速模式切换用 */
  onClearScenario?: () => void;
  onDismissScenario?: () => void;
}

const CoworkView: React.FC<CoworkViewProps> = ({ onShowSkills, isSidebarCollapsed, onToggleSidebar, onNewChat, authState, isMobile, activeScenario, onSelectScenario, onShowScenarios, onClearScenario, onDismissScenario }) => {
  const dispatch = useDispatch();
  const sessionIdentity = authState ? authService.getSessionIdentity() : null;
  const isMac = window.electron.platform === 'darwin';
  const [isInitialized, setIsInitialized] = useState(false);
  const [sandboxProfiles, setSandboxProfiles] = useState<SandboxProfile[]>([]);
  const [selectedSandboxProfileId, setSelectedSandboxProfileId] = useState<string | null>(null);
  const [sandboxImages, setSandboxImages] = useState<SandboxImage[]>([]);
  const [selectedSandboxImageId, setSelectedSandboxImageId] = useState<string | null>(null);
  // 执行环境（云端沙箱 / 桌面设备）与工作目录放在 Redux，与侧边栏的环境选择器共用同一份状态
  const selectedDesktopClientId = useSelector((state: RootState) => state.cowork.selectedDesktopClientId);
  const selectedDesktopClientName = useSelector((state: RootState) => state.cowork.selectedDesktopClientName);
  const selectedDesktopCwd = useSelector((state: RootState) => state.cowork.selectedDesktopCwd);
  const selectedDesktopPermissionMode = useSelector((state: RootState) => state.cowork.selectedDesktopPermissionMode);
  // Override sandbox config for sessions that lack it (e.g. desktop-origin)
  const [sessionSandboxProfileId, setSessionSandboxProfileId] = useState<string | null>(null);
  const [sessionSandboxImageId, setSessionSandboxImageId] = useState<string | null>(null);
  // 覆盖值只属于当时那个会话；切换会话必须清空，否则会串到下一个会话的 chip 上，
  // 续聊时还会把别的模板带过去。
  const currentSessionId = useSelector((state: RootState) => state.cowork.currentSessionId);
  useEffect(() => {
    setSessionSandboxProfileId(null);
    setSessionSandboxImageId(null);
  }, [currentSessionId]);
  // 新建会话仍需防重复；续聊使用 Promise 链保留每次提交并维持提交顺序。
  const isStartingRef = useRef(false);
  const continuationSubmissionChainRef = useRef<Promise<void>>(Promise.resolve());
  // Track pending start request so stop can cancel delayed startup.
  const pendingStartRef = useRef<{ requestId: number; cancelled: boolean } | null>(null);
  const startRequestIdRef = useRef(0);
  // Ref for CoworkPromptInput
  const promptInputRef = useRef<CoworkPromptInputRef>(null);
  // Stable staging ID for sandbox file uploads before a session is created
  const uploadStagingIdRef = useRef(crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) + Date.now().toString(36));
  // Track which session was started from a scenario
  const scenarioSessionIdRef = useRef<string | null>(null);
  // Keep latest activeScenario in a ref so async callbacks always see the current value
  const activeScenarioRef = useRef(activeScenario);
  activeScenarioRef.current = activeScenario;

  // 全局拖拽文件状态
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dragDepthRef = useRef(0);

  // 使用 document 级原生事件监听，确保能拦截浏览器默认的文件打开行为
  useEffect(() => {
    const isFileDrag = (e: DragEvent) =>
      Array.from(e.dataTransfer?.types ?? []).includes('Files');

    const onDragEnter = (e: DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      dragDepthRef.current++;
      if (dragDepthRef.current === 1) setIsDraggingOver(true);
    };

    const onDragOver = (e: DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };

    const onDragLeave = (e: DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) setIsDraggingOver(false);
    };

    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      dragDepthRef.current = 0;
      setIsDraggingOver(false);
      // 如果已被 CoworkPromptInput 处理则跳过
      if ((e as any)._boclawFileHandled) return;
      if (e.dataTransfer?.files.length) {
        promptInputRef.current?.addFiles(e.dataTransfer.files);
      }
    };

    document.addEventListener('dragenter', onDragEnter);
    document.addEventListener('dragover', onDragOver);
    document.addEventListener('dragleave', onDragLeave);
    document.addEventListener('drop', onDrop);
    return () => {
      document.removeEventListener('dragenter', onDragEnter);
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('dragleave', onDragLeave);
      document.removeEventListener('drop', onDrop);
    };
  }, []);

  const {
    currentSession,
    isStreaming,
    config,
  } = useSelector((state: RootState) => state.cowork);

  const activeSkillIds = useSelector((state: RootState) => state.skill.activeSkillIds);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [selectedAgentModel, setSelectedAgentModel] = useState<string | null>(null);
  useEffect(() => {
    const clear = () => { setSelectedAgentId(''); setSelectedAgentModel(null); };
    window.addEventListener('auth:identityChanging', clear);
    return () => window.removeEventListener('auth:identityChanging', clear);
  }, []);
  const newTaskPromptMode = useSelector((state: RootState) => state.cowork.newTaskPromptMode);
  const selectedModel = useSelector((state: RootState) => state.model.selectedModel);
  const skills = useSelector((state: RootState) => state.skill.skills);

  // 已打开的会话决定模型来源；无会话时才使用新建任务选择的桌面设备。
  const modelClientId = currentSession
    ? currentSession.origin === 'desktop' ? getTargetClient(currentSession.id) : null
    : selectedDesktopClientId;
  useLayoutEffect(() => {
    let cancelled = false;

    if (modelClientId || currentSession?.origin === 'desktop') {
      dispatch(setAvailableModels([]));
      dispatch(setSelectedModel({ id: '', name: '' }));
    }
    if (!modelClientId) {
      if (currentSession?.origin !== 'desktop') authService.loadSavedModelsToRedux();
      return () => { cancelled = true; };
    }

    void relayService.getCoworkConfig(modelClientId).then((desktopConfig) => {
      if (cancelled) return;
      dispatch(setAvailableModels(desktopConfig.availableModels));

      const preferredModel = findRelayModel(desktopConfig.availableModels, desktopConfig.model)
        ?? desktopConfig.availableModels[0];
      if (preferredModel) {
        dispatch(setSelectedModel(preferredModel));
      }
    }).catch((error) => {
      if (!cancelled) {
        console.warn('[CoworkView] Failed to load desktop model list:', error);
        dispatch(setAvailableModels([]));
      }
    });

    return () => { cancelled = true; };
  }, [modelClientId, currentSession?.origin, dispatch]);

  useEffect(() => {
    coworkService.getSandboxProfiles().then(profiles => {
      setSandboxProfiles(profiles);
      if (profiles.length > 0) setSelectedSandboxProfileId(prev => prev ?? profiles[0].id);
    }).catch(() => {});
    coworkService.getSandboxImageCatalog().then(images => {
      setSandboxImages(images);
      if (images.length > 0) setSelectedSandboxImageId(prev => prev ?? images[0].id);
    }).catch(() => {});

    // Refresh sandbox data when platform pushes sync events
    const unsubscribe = window.electron.cowork.onPlatformSandboxSynced?.(() => {
      coworkService.getSandboxProfiles().then(profiles => {
        setSandboxProfiles(profiles);
        if (profiles.length > 0) setSelectedSandboxProfileId(prev => prev ?? profiles[0].id);
      }).catch(() => {});
      coworkService.getSandboxImageCatalog().then(images => {
        setSandboxImages(images);
        if (images.length > 0) setSelectedSandboxImageId(prev => prev ?? images[0].id);
      }).catch(() => {});
    });
    return () => { unsubscribe?.(); };
  }, []);

  // 登录后自动加载会话和沙箱数据，退出登录后自动清空
  useEffect(() => {
    let alive = true;
    if (sessionIdentity) {
      coworkService.loadSessions();
      relayService.ensureConnected()
        .then(() => {
          if (alive) return relayService.discoverDesktopSessions();
        })
        .catch(() => {});
      coworkService.getSandboxProfiles().then(profiles => {
        if (!alive) return;
        setSandboxProfiles(profiles);
        if (profiles.length > 0) setSelectedSandboxProfileId(prev => prev ?? profiles[0].id);
      }).catch(() => {});
      coworkService.getSandboxImageCatalog().then(images => {
        if (!alive) return;
        setSandboxImages(images);
        if (images.length > 0) setSelectedSandboxImageId(prev => prev ?? images[0].id);
      }).catch(() => {});
    } else {
      dispatch(setSessions([]));
      dispatch(clearCurrentSession());
      setSandboxProfiles([]);
      setSandboxImages([]);
    }
    return () => { alive = false; };
  }, [sessionIdentity, dispatch]);

  useEffect(() => {
    let current = true;
    setIsInitialized(false);
    if (!sessionIdentity) return;
    const init = async () => {
      await coworkService.init();
      if (current) setIsInitialized(true);
    };
    void init();
    return () => { current = false; };
  }, [sessionIdentity]);

  const handleStartSession = async (prompt: string, skillPrompt?: string, attachments: CoworkAttachment[] = []): Promise<boolean> => {
    // If a desktop client is selected, delegate to desktop session handler
    if (selectedDesktopClientId) {
      return handleStartDesktopSession(prompt, selectedDesktopClientId, skillPrompt, attachments);
    }
    if (!authState) {
      return false;
    }
    // Use ref to get the latest activeScenario value (survives across awaits)
    const currentScenario = activeScenarioRef.current;
    // Record scenario usage on first actual message send
    if (currentScenario) {
      scenarioService.recordUsage(currentScenario.id);
    }
    // Prevent duplicate submissions
    if (isStartingRef.current) return false;
    isStartingRef.current = true;
    const requestId = ++startRequestIdRef.current;
    pendingStartRef.current = { requestId, cancelled: false };
    const isPendingStartCancelled = () => {
      const pending = pendingStartRef.current;
      return !pending || pending.requestId !== requestId || pending.cancelled;
    };

    try {
      try {
        const apiConfig = await coworkService.checkApiConfig();
        if (apiConfig && !apiConfig.hasConfig) {
          window.dispatchEvent(new CustomEvent('app:showToast', {
            detail: i18nService.t('coworkModelSettingsRequired'),
          }));
          isStartingRef.current = false;
          return false;
        }
      } catch (error) {
        console.error('Failed to check cowork API config:', error);
      }

      if (newTaskPromptMode !== 'quick' && !selectedSandboxProfileId) {
        window.dispatchEvent(new CustomEvent('app:showToast', {
          detail: i18nService.t('coworkSandboxProfileRequired'),
        }));
        isStartingRef.current = false;
        return false;
      }

      if (newTaskPromptMode !== 'quick' && !selectedSandboxImageId) {
        window.dispatchEvent(new CustomEvent('app:showToast', {
          detail: i18nService.t('coworkSandboxImageRequired'),
        }));
        isStartingRef.current = false;
        return false;
      }

      // Create a temporary session with user message to show immediately
      const tempSessionId = `temp-${Date.now()}`;
      const fallbackTitle = prompt.split('\n')[0].slice(0, 50) || i18nService.t('coworkNewSession');
      const now = Date.now();

      // Capture active skill IDs before clearing them
      const sessionSkillIds = [...activeSkillIds];

      const tempSession: CoworkSession = {
        id: tempSessionId,
        title: fallbackTitle,
        claudeSessionId: null,
        status: 'running',
        pinned: false,
        createdAt: now,
        updatedAt: now,
        cwd: config.workingDirectory || '',
        systemPrompt: '',
        executionMode: 'sandbox',
        activeSkillIds: sessionSkillIds,
        sandboxProfileId: selectedSandboxProfileId,
        sandboxImageId: selectedSandboxImageId,
        messages: [
          {
            id: `msg-${now}`,
            type: 'user',
            content: prompt,
            timestamp: now,
            metadata: sessionSkillIds.length > 0 ? { skillIds: sessionSkillIds } : undefined,
          },
        ],
      };

      // Immediately show the session detail page with user message
      dispatch(setCurrentSession(tempSession));
      dispatch(setStreaming(true));

      // Track scenario-session association
      if (currentScenario) {
        scenarioSessionIdRef.current = tempSessionId;
      }

      // Clear active skills and quick action selection after starting session
      // so they don't persist to next session
      dispatch(clearActiveSkills());
      dispatch(clearSelection());

      // Combine skill prompt with system prompt
      // If no manual skill selected, use auto-routing prompt
      // 快速模式：受限联网轻量对话，不带技能/场景提示词（服务端会用极简提示词覆盖）
      let effectiveSkillPrompt = newTaskPromptMode === 'quick' ? undefined : skillPrompt;
      if (!skillPrompt && newTaskPromptMode !== 'quick') {
        effectiveSkillPrompt = await skillService.getAutoRoutingPrompt() || undefined;
      }
      // 优先使用 scenario 的 system prompt（专家身份 / 描述 / 流程 / 技能 / knowledge）
      let effectiveSystemPrompt = '';
      if (currentScenario && newTaskPromptMode !== 'quick') {
        const scenarioPrompt = buildScenarioSystemPrompt(currentScenario);
        if (scenarioPrompt) {
          effectiveSystemPrompt = scenarioPrompt;
        }
      }
      const combinedSystemPrompt = [effectiveSkillPrompt, effectiveSystemPrompt]
        .filter(p => p?.trim())
        .join('\n\n') || undefined;

      if (isPendingStartCancelled()) {
        return false;
      }

      // Start the actual session immediately with fallback title
      const startedSession = await coworkService.startSession({
        prompt,
        title: fallbackTitle,
        cwd: config.workingDirectory || undefined,
        systemPrompt: combinedSystemPrompt,
        activeSkillIds: newTaskPromptMode === 'quick' ? [] : sessionSkillIds,
        sandboxProfileId: selectedSandboxProfileId,
        sandboxImageId: selectedSandboxImageId,
        modelId: (selectedModel?.id ? sessionModelReference(selectedModel) : undefined),
        promptMode: newTaskPromptMode,
        ...(newTaskPromptMode !== 'quick' && !activeScenario && selectedAgentId ? { agentId: selectedAgentId } : {}),
        // 从场景应用进入时把场景标识交给服务端持久化，重开/换设备仍能显示场景卡片
        scenario: newTaskPromptMode === 'quick' ? null : createSessionScenario(currentScenario),
      });

      // Update scenario-session mapping with real session ID
      if (startedSession && scenarioSessionIdRef.current === tempSessionId) {
        scenarioSessionIdRef.current = startedSession.id;
      }

      // Generate title in background and update when ready
      if (startedSession) {
        coworkService.generateSessionTitle(prompt).then(generatedTitle => {
          const title = generatedTitle?.trim();
          if (title && title !== 'New Session') {
            coworkService.renameSession(startedSession.id, title);
          }
        }).catch(() => {});
      }

      // Stop immediately if user cancelled while startup request was in flight.
      if (isPendingStartCancelled() && startedSession) {
        await coworkService.stopSession(startedSession.id);
      }
      return Boolean(startedSession);
    } finally {
      if (pendingStartRef.current?.requestId === requestId) {
        pendingStartRef.current = null;
      }
      isStartingRef.current = false;
    }
  };

  const handleStartDesktopSession = async (
    prompt: string,
    targetClientId: string,
    skillPrompt?: string,
    attachments: CoworkAttachment[] = [],
  ): Promise<boolean> => {
    if (!authState) return false;
    if (isStartingRef.current) return false;
    isStartingRef.current = true;

    try {
      // 1. Create temporary session (immediate UI feedback)
      const tempSessionId = `temp-desktop-${Date.now()}`;
      const fallbackTitle = prompt.split('\n')[0].slice(0, 50)
        || attachments[0]?.name
        || i18nService.t('coworkNewSession');
      const now = Date.now();
      const sessionSkillIds = [...activeSkillIds];
      // Use ref to get the latest activeScenario value (survives across the awaits below)
      const currentScenario = activeScenarioRef.current;

      const previewPrompt = withAttachmentPaths(prompt, attachments.map(item => item.name));
      const tempSession: CoworkSession = {
        id: tempSessionId,
        title: fallbackTitle,
        claudeSessionId: null,
        status: 'running',
        pinned: false,
        createdAt: now,
        updatedAt: now,
        cwd: '',
        systemPrompt: '',
        executionMode: 'sandbox',
        activeSkillIds: sessionSkillIds,
        modelId: selectedModel?.id ? relayModelKey(selectedModel) : null,
        permissionMode: selectedDesktopPermissionMode,
        origin: 'desktop',
        messages: [{
          id: `local-${now}`,
          type: 'user',
          content: previewPrompt,
          timestamp: now,
          metadata: sessionSkillIds.length > 0 ? { skillIds: sessionSkillIds } : undefined,
        }],
      };

      dispatch(setCurrentSession(tempSession));
      dispatch(setStreaming(true));

      // Track scenario-session association (same as the cloud path) — without this,
      // CoworkSessionDetail's `currentSession.id === scenarioSessionIdRef.current`
      // check never matches for desktop sessions and the scenario card never shows
      // once the temp session is replaced by the real one.
      if (currentScenario) {
        scenarioSessionIdRef.current = tempSessionId;
        scenarioService.recordUsage(currentScenario.id);
      }

      // Clear active skill/quick-action selection so it doesn't persist to the next session
      dispatch(clearActiveSkills());
      dispatch(clearSelection());

      // 2. Relay 只下发场景能力、流程、技能和知识；产品/专家身份由 Desktop 统一注入。
      let effectiveSystemPrompt = '';
      if (currentScenario) {
        const scenarioPrompt = buildScenarioSystemPrompt(currentScenario, { includeIdentity: false });
        if (scenarioPrompt) effectiveSystemPrompt = scenarioPrompt;
      }
      const combinedSystemPrompt = [skillPrompt, effectiveSystemPrompt]
        .filter(p => p?.trim())
        .join('\n\n') || undefined;

      // 3. Ensure Relay WS is connected
      await relayService.ensureConnected();

      // Upload to the desktop before creating the real session. A pending id
      // lets Desktop choose the requested cwd; session.start then registers the
      // returned absolute paths in the newly-created session.
      const pendingUploadId = `pending-web-${now}`;
      const attachmentPaths: string[] = [];
      for (const attachment of attachments) {
        if (!attachment.file) {
          throw new Error(`无法读取附件内容: ${attachment.name}`);
        }
        if (attachment.file.size > DESKTOP_ATTACHMENT_MAX_BYTES) {
          throw new Error(`附件 ${attachment.name} 超过 20MB 限制`);
        }
        const dataBase64 = await fileToBase64(attachment.file);
        const uploaded = await relayService.uploadFile(
          targetClientId,
          pendingUploadId,
          attachment.name,
          dataBase64,
          selectedDesktopCwd || undefined,
        );
        attachmentPaths.push(uploaded.path);
      }
      const desktopPrompt = withAttachmentPaths(prompt, attachmentPaths);

      // 4. Send session.start to Desktop via Relay
      let result: { session: any };
      try {
        result = await relayService.startSession(targetClientId, {
          scenario: createSessionScenario(currentScenario),
          prompt: desktopPrompt,
          systemPrompt: combinedSystemPrompt,
          activeSkillIds: sessionSkillIds,
          modelId: selectedModel?.id ? relayModelKey(selectedModel) : undefined,
          permissionMode: selectedDesktopPermissionMode,
          // 用户在 composer 选定的桌面工作目录；未选时不传，由桌面端用自己的默认目录
          cwd: selectedDesktopCwd || undefined,
          attachmentPaths,
        });
      } catch (err) {
        dispatch(setStreaming(false));
        dispatch(updateSessionStatus({ sessionId: tempSessionId, status: 'error' }));
        window.dispatchEvent(new CustomEvent('app:showToast', {
          detail: `${i18nService.t('relayDesktopOffline')}: ${(err as Error).message}`,
        }));
        return false;
      }

      // 5. Replace temp session with real session from Desktop
      if (result?.session) {
        const realSession: CoworkSession = {
          id: result.session.id,
          scenario: result.session.scenario ?? null,
          title: result.session.title || fallbackTitle,
          claudeSessionId: result.session.claudeSessionId || null,
          status: 'running',
          pinned: false,
          createdAt: result.session.createdAt || now,
          updatedAt: result.session.updatedAt || now,
          cwd: result.session.cwd || result.session.workingDirectory || '',
          systemPrompt: result.session.systemPrompt || '',
          executionMode: result.session.executionMode || 'sandbox',
          activeSkillIds: result.session.activeSkillIds || [],
          modelId: result.session.modelId ?? result.session.model ?? (selectedModel?.id ? relayModelKey(selectedModel) : null),
          permissionMode: result.session.permissionMode ?? selectedDesktopPermissionMode,
          origin: 'desktop',  // Force override (Desktop stores 'app')
          messages: tempSession.messages.map((message, index) => (
            index === 0 ? { ...message, content: desktopPrompt } : message
          )),
        };

        dispatch(addSession(realSession));

        relayService.associateDesktopSession(realSession, targetClientId);

        // Update scenario-session mapping with the real session ID
        if (scenarioSessionIdRef.current === tempSessionId) {
          scenarioSessionIdRef.current = realSession.id;
        }
      }
      return Boolean(result?.session);
    } catch (err) {
      dispatch(setStreaming(false));
      window.dispatchEvent(new CustomEvent('app:showToast', {
        detail: `附件上传失败: ${(err as Error).message}`,
      }));
      return false;
    } finally {
      isStartingRef.current = false;
    }
  };

  const handleContinueSession = async (prompt: string, skillPrompt?: string, attachments: CoworkAttachment[] = []): Promise<boolean> => {
    if (!authState) {
      return false;
    }
    if (!currentSession) return false;
    // 冻结本次提交的身份与场景；排队、上传或查询期间不能借用后来切换到的会话上下文。
    const continuationScenario = activeScenarioRef.current;
    const submissionIdentity = authService.getSessionIdentity();
    const submissionPlatform = authService.getBaseUrl();
    const identityIsCurrent = () => submissionIdentity && authService.getSessionIdentity() === submissionIdentity
      && authService.getBaseUrl() === submissionPlatform;

    // 每次调用都先占一个链节点，后续调用只能在前一条完成入队/发起后继续；
    // 不能用布尔互斥直接 return，否则快速连续提交会静默丢消息。
    const previousSubmission = continuationSubmissionChainRef.current;
    let releaseSubmission!: () => void;
    continuationSubmissionChainRef.current = new Promise<void>((resolve) => {
      releaseSubmission = resolve;
    });
    await previousSubmission.catch(() => undefined);

    try {
      if (!identityIsCurrent()) return false;
      const scenarioSkills = continuationScenario && currentSession.origin !== 'desktop'
        ? await resolveScenarioSkills(continuationScenario, { force: true })
        : null;
      if (!identityIsCurrent()) return false;
      // 桌面沿用其提供商别名；云端保留明确模型身份，失效时由服务端拒绝，不能悄悄换价。
      const desktopTargetClientId = currentSession.origin === 'desktop' ? getTargetClient(currentSession.id) : null;
      const sessionModelId = currentSession.origin === 'desktop'
        ? currentSession.modelId || (selectedModel?.id ? relayModelKey(selectedModel) : undefined)
        : currentSession.modelId || (selectedModel?.id ? sessionModelReference(selectedModel) : undefined);
      if (currentSession.origin === 'desktop' && !desktopTargetClientId) throw new Error(i18nService.t('relayDesktopOffline'));

      // Desktop session: route through Relay
      if (currentSession.origin === 'desktop' && desktopTargetClientId) {
        await relayService.ensureConnected();
        const attachmentPaths: string[] = [];
        for (const attachment of attachments) {
          if (!attachment.file) throw new Error(`无法读取附件内容: ${attachment.name}`);
          if (attachment.file.size > DESKTOP_ATTACHMENT_MAX_BYTES) {
            throw new Error(`附件 ${attachment.name} 超过 20MB 限制`);
          }
          const uploaded = await relayService.uploadFile(
            desktopTargetClientId,
            currentSession.id,
            attachment.name,
            await fileToBase64(attachment.file),
            currentSession.cwd || undefined,
          );
          attachmentPaths.push(uploaded.path);
        }
        const desktopPrompt = withAttachmentPaths(prompt, attachmentPaths);
        // If streaming, stop first
        if (isStreaming) {
          try {
            await relayService.stopSession(desktopTargetClientId, currentSession.id);
          } catch {}
          dispatch(setStreaming(false));
        }

        // Add user message to Redux
        const now = Date.now();
        dispatch(addMessage({
          sessionId: currentSession.id,
          message: { id: `local-${now}`, type: 'user', content: desktopPrompt, timestamp: now },
        }));
        dispatch(setStreaming(true));
        dispatch(updateSessionStatus({ sessionId: currentSession.id, status: 'running' }));

        // Capture active skill IDs, falling back to the session's own skills if
        // none were (re)selected for this turn — same rule as the cloud path.
        let sessionSkillIds = [...activeSkillIds];
        if (sessionSkillIds.length > 0) {
          dispatch(clearActiveSkills());
        }
        if (sessionSkillIds.length === 0 && currentSession.activeSkillIds?.length > 0) {
          sessionSkillIds = [...currentSession.activeSkillIds];
        }
        let effectiveSkillPrompt = skillPrompt;
        if (!skillPrompt && sessionSkillIds.length > 0) {
          const sessionSkills = sessionSkillIds
            .map(id => skills.find(s => s.id === id))
            .filter((s): s is Skill => s !== undefined);
          if (sessionSkills.length > 0) {
            effectiveSkillPrompt = sessionSkills.map(buildInlinedSkillPrompt).join('\n\n');
          }
        }

        // Build systemPrompt — keep the scenario's persona alive across turns.
        // Previously only handleStartDesktopSession applied it; continuing a
        // desktop session silently dropped back to the plain config prompt.
        const continueScenario = continuationScenario;
        let effectiveSystemPrompt = '';
        if (continueScenario) {
          const scenarioPrompt = buildScenarioSystemPrompt(continueScenario);
          if (scenarioPrompt) effectiveSystemPrompt = scenarioPrompt;
        }
        const combinedSystemPrompt = [effectiveSkillPrompt, effectiveSystemPrompt]
          .filter(p => p?.trim())
          .join('\n\n') || undefined;

        // Send via Relay
        try {
          await relayService.ensureConnected();
          relayService.claimSession(currentSession.id);
          await relayService.continueSession(
            desktopTargetClientId, currentSession.id, desktopPrompt,
            combinedSystemPrompt, sessionModelId,
            sessionSkillIds.length > 0 ? sessionSkillIds : undefined,
            createSessionScenario(continueScenario),
          );
        } catch (err) {
          dispatch(updateSessionStatus({ sessionId: currentSession.id, status: 'error' }));
          window.dispatchEvent(new CustomEvent('app:showToast', {
            detail: `${i18nService.t('relayDesktopOffline')}: ${(err as Error).message}`,
          }));
          dispatch(setStreaming(false));
          return false;
        }

        return true; // 桌面端已确认接收，不进入云端流程
      }

      // Capture active skill IDs before clearing
      let sessionSkillIds = [...new Set([...activeSkillIds, ...(scenarioSkills?.ready.map(skill => skill.id) ?? [])])];

      // Clear active skills after capturing so they don't persist to next message
      if (sessionSkillIds.length > 0 && store.getState().cowork.currentSessionId === currentSession.id) {
        dispatch(clearActiveSkills());
      }

      // 如果本次没有手动选技能，沿用 session 首次的技能
      if (!continuationScenario && sessionSkillIds.length === 0 && currentSession.activeSkillIds?.length > 0) {
        sessionSkillIds = [...currentSession.activeSkillIds];
      }

      // Combine skill prompt with system prompt for continuation
      // If no manual skill selected but session has skills, rebuild skill prompt from session skills
      let effectiveSkillPrompt = skillPrompt;
      if (!skillPrompt && sessionSkillIds.length > 0) {
        const sessionSkills = sessionSkillIds
          .map(id => scenarioSkills?.ready.find(skill => skill.id === id) || skills.find(s => s.id === id))
          .filter((s): s is Skill => s !== undefined);
        if (sessionSkills.length > 0) {
          effectiveSkillPrompt = sessionSkills.map(buildInlinedSkillPrompt).join('\n\n');
        }
      }
      if (!effectiveSkillPrompt) {
        effectiveSkillPrompt = await skillService.getAutoRoutingPrompt() || undefined;
      }

      // 优先使用 scenario 的 system prompt（专家身份 / 描述 / 流程 / 技能 / knowledge）
      const continueScenario = continuationScenario;
      let effectiveSystemPrompt = '';
      if (continueScenario) {
        const scenarioPrompt = buildScenarioSystemPrompt(continueScenario);
        if (scenarioPrompt) {
          effectiveSystemPrompt = scenarioPrompt;
        }
      }
      const combinedSystemPrompt = [effectiveSkillPrompt, effectiveSystemPrompt]
        .filter(p => p?.trim())
        .join('\n\n') || undefined;

      const effectiveSandboxProfileId = currentSession.sandboxProfileId || sessionSandboxProfileId || undefined;
      const effectiveSandboxImageId = currentSession.sandboxImageId || sessionSandboxImageId || undefined;

      // Update Redux state BEFORE continueSession to avoid overwriting the user message it adds
      if (sessionSandboxProfileId || sessionSandboxImageId) {
        dispatch(setCurrentSession({
          ...currentSession,
          sandboxProfileId: effectiveSandboxProfileId ?? null,
          sandboxImageId: effectiveSandboxImageId ?? null,
        }));
        setSessionSandboxProfileId(null);
        setSessionSandboxImageId(null);
      }

      if (!identityIsCurrent()) return false;
      const submission = await coworkService.submitOrQueueContinuation({
        scenario: createSessionScenario(continueScenario),
        sessionId: currentSession.id,
        prompt,
        systemPrompt: combinedSystemPrompt,
        activeSkillIds: continuationScenario || sessionSkillIds.length > 0 ? sessionSkillIds : undefined,
        sandboxProfileId: effectiveSandboxProfileId,
        sandboxImageId: effectiveSandboxImageId,
        modelId: sessionModelId,
      }, {
        preview: prompt.trim().slice(0, 160),
      });
      return submission !== 'failed';
    } catch (err) {
      dispatch(setStreaming(false));
      window.dispatchEvent(new CustomEvent('app:showToast', {
        detail: `消息发送失败: ${(err as Error).message}`,
      }));
      return false;
    } finally {
      releaseSubmission();
    }
  };

  const handleStopSession = async () => {
    if (!currentSession) return;
    // Desktop session: stop via Relay
    if (currentSession.origin === 'desktop') {
      const targetClientId = getTargetClient(currentSession.id);
      if (targetClientId) {
        try {
          await relayService.stopSession(targetClientId, currentSession.id);
        } catch (err) {
          console.warn('[CoworkView] Failed to stop Desktop session via relay:', err);
        }
      }
      return;
    }
    if (currentSession.id.startsWith('temp-') && pendingStartRef.current) {
      pendingStartRef.current.cancelled = true;
    }
    await coworkService.stopSession(currentSession.id);
  };

  useEffect(() => {
    const handleNewSession = () => {
      dispatch(clearCurrentSession());
      dispatch(clearSelection());
      window.dispatchEvent(new CustomEvent('cowork:focus-input', {
        detail: { clear: true },
      }));
    };
    window.addEventListener('cowork:shortcut:new-session', handleNewSession);
    return () => {
      window.removeEventListener('cowork:shortcut:new-session', handleNewSession);
    };
  }, [dispatch]);

  if (!isInitialized) {
    return (
      <div className="flex-1 h-full flex flex-col dark:bg-claude-darkBg bg-claude-bg">
        <div className="draggable flex h-12 items-center justify-end px-4 border-b dark:border-claude-darkBorder border-claude-border shrink-0">
          <WindowTitleBar inline />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="dark:text-claude-darkTextSecondary text-claude-textSecondary">
            {i18nService.t('loading')}
          </div>
        </div>
      </div>
    );
  }

  // When there's a current session, show the session detail view
  if (currentSession) {
    return (
      <>
        <CoworkSessionDetail
          onManageSkills={() => onShowSkills?.()}
          onContinue={handleContinueSession}
          onStop={handleStopSession}
          onNavigateHome={() => dispatch(clearCurrentSession())}
          isSidebarCollapsed={isSidebarCollapsed}
          onToggleSidebar={onToggleSidebar}
          onNewChat={onNewChat}
          isMobile={isMobile}
          overrideSandboxProfileId={sessionSandboxProfileId}
          onSandboxProfileOverride={setSessionSandboxProfileId}
          overrideSandboxImageId={sessionSandboxImageId}
          onSandboxImageOverride={setSessionSandboxImageId}
          activeScenario={activeScenario && (currentSession.id === scenarioSessionIdRef.current || currentSession.scenario?.id === activeScenario.id || scenarioService.getSessionScenario(currentSession.id)?.id === activeScenario.id) ? activeScenario : null}
          onSelectScenario={canApplyScenarioToConversation(currentSession) ? onSelectScenario : undefined}
          onDismissScenario={() => {
            scenarioSessionIdRef.current = null;
            onDismissScenario?.();
          }}
        />
      </>
    );
  }

  // Home view - no current session
  // 输入框实例(唯一):无场景时进入居中内容流,有场景时贴底 —— 复用同一份 JSX
  const promptInputElement = (
    <div>
      <CoworkPromptInput
        ref={promptInputRef}
        onSubmit={handleStartSession}
        onStop={handleStopSession}
        isStreaming={isStreaming}
        placeholder={i18nService.t('coworkPlaceholder')}
        size="large"
        showModelSelector={!(selectedAgentModel && !selectedDesktopClientId && !activeScenario && newTaskPromptMode !== 'quick')}
        showPromptModeToggle={true}
        activeScenario={activeScenario}
        onSelectScenario={onSelectScenario}
        showSandboxSelector={true}
        sandboxDisabled={isStreaming && !!selectedDesktopClientId}
        executionMode="sandbox"
        sessionId={uploadStagingIdRef.current}
        sessionIsDraft
        sandboxProfiles={sandboxProfiles}
        selectedSandboxProfileId={selectedSandboxProfileId}
        onSandboxProfileChange={setSelectedSandboxProfileId}
        sandboxImages={sandboxImages}
        selectedSandboxImageId={selectedSandboxImageId}
        onSandboxImageChange={setSelectedSandboxImageId}
        selectedDesktopClientId={selectedDesktopClientId}
        selectedDesktopClientName={selectedDesktopClientName}
        onDesktopClientChange={(clientId) => {
          // Eagerly cache device name so the button label survives even when
          // relayClients becomes empty (e.g. WS reconnecting while session runs).
          const deviceName = clientId
            ? relayService.getAllClients().find(c => c.clientId === clientId)?.deviceName ?? null
            : null;
          dispatch(setSelectedDesktopClient({ clientId, deviceName }));
        }}
        desktopCwd={selectedDesktopCwd}
        onDesktopCwdChange={(cwd) => dispatch(setSelectedDesktopCwd(cwd))}
        onManageSkills={() => onShowSkills?.()}
      />
    </div>
  );

  return (
    <div className="flex-1 flex flex-col dark:bg-claude-darkBg bg-claude-bg h-full relative">
      {/* 全局拖拽文件遮罩 */}
      {isDraggingOver && (
        <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center bg-claude-accent/5 dark:bg-claude-accent/10 border-2 border-dashed border-claude-accent/40 rounded-lg">
          <div className="flex flex-col items-center gap-2 text-claude-accent">
            <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            <span className="text-sm font-medium">{i18nService.t('coworkDropFileHint')}</span>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="draggable flex h-[60px] shrink-0 items-center justify-between px-5">
        <div className="non-draggable flex min-w-0 items-center gap-2.5">
          {isSidebarCollapsed && (
            <div className={`flex items-center gap-1 ${isMac && !isMobile ? 'pl-[68px]' : ''}`}>
              <button
                type="button"
                onClick={onToggleSidebar}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-claude-textSecondary transition-colors hover:bg-claude-surfaceHover hover:text-claude-text dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover dark:hover:text-claude-darkText"
              >
                <SidebarToggleIcon className="h-4 w-4" isCollapsed={true} />
              </button>
              <button
                type="button"
                onClick={onNewChat}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-claude-textSecondary transition-colors hover:bg-claude-surfaceHover hover:text-claude-text dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover dark:hover:text-claude-darkText"
              >
                <ComposeIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
        <WindowTitleBar inline />
      </div>

      {/* Main Content — 默认首页保持留白；场景模式继续展示场景内容。 */}
      <div className={`flex-1 min-h-0 overflow-y-auto ${activeScenario ? 'flex justify-center pt-6 pb-4' : ''}`}>
        <div className={`mx-auto h-full w-full px-4 ${activeScenario ? 'max-w-3xl space-y-4' : ''}`}>
          {!activeScenario && (
            <div className="flex min-h-full items-start justify-center px-2 pb-6 pt-[8vh] md:px-4 md:pt-[12vh]">
              <div className="w-full max-w-[900px] text-center">
                <h1 className="text-[28px] font-bold leading-tight tracking-tight text-claude-text md:text-[32px] dark:text-claude-darkText">
                  你好，我是BoMate
                </h1>
                <p className="mx-auto mt-5 max-w-[900px] text-base leading-8 text-claude-textSecondary dark:text-claude-darkTextSecondary">
                  BoMate 是面向投资研究与资产管理的 AI 助手，帮助你管理项目与基金档案、分<br />
                  析财报与行业数据、开展尽职调查，并沉淀可追溯的投资研究成果。
                </p>
                <div className="mt-7 flex justify-center">
                  <CoworkPromptModeSwitch
                    value={newTaskPromptMode}
                    onChange={(mode) => {
                      if (mode === newTaskPromptMode) return;
                      if (mode === 'quick') onClearScenario?.();
                      dispatch(setNewTaskPromptMode(mode));
                    }}
                  />
                </div>
                {!selectedDesktopClientId && newTaskPromptMode !== 'quick' && onSelectScenario && (
                  <RecentScenarioCards onSelectScenario={onSelectScenario} onShowAll={onShowScenarios} />
                )}
              </div>
            </div>
          )}

          {!selectedDesktopClientId && !activeScenario && newTaskPromptMode !== 'quick' && <CustomAgentPicker value={selectedAgentId} onChange={setSelectedAgentId} onModelChange={setSelectedAgentModel} />}
          <CampaignCard slot="home_bubble" />

          {/* Scenario Banner — layoutId morph + staggered entry animations */}
          {activeScenario && (() => {
            // 解析场景应用封装字段（workflow_steps 仅用于 system prompt 注入，不在此处展示）
            const triggerPrompts = parseScenarioTriggers(activeScenario.trigger_prompts);
            const greeting = activeScenario.greeting || '';
            const fillPromptIntoInput = (text: string) => {
              promptInputRef.current?.setValue(text);
              promptInputRef.current?.focus();
            };
            return (
            <div className="space-y-3">
              <motion.div
                layoutId={`scenario-card-${activeScenario.id}`}
                className="relative mx-auto max-w-lg rounded-2xl border dark:border-claude-accent/30 border-claude-accent/20 dark:bg-claude-accent/5 bg-claude-accent/[0.03] px-5 py-4 shadow-sm"
                transition={{ type: 'spring', stiffness: 180, damping: 24 }}
                style={{ willChange: 'transform, opacity' }}
              >
                <button
                  type="button"
                  onClick={onDismissScenario}
                  className="absolute top-3 right-3 text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary hover:text-claude-accent transition-colors"
                >
                  {i18nService.t('scenariosDismiss')}
                </button>
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-claude-accent/10 text-claude-accent overflow-hidden text-xl">
                    <ScenarioAppIcon scenario={activeScenario} size={40} className="rounded-xl" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold dark:text-claude-darkText text-claude-text">{activeScenario.name}</span>
                      {activeScenario.group_name && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-claude-accent/10 text-claude-accent font-medium">{activeScenario.group_name}</span>
                      )}
                    </div>
                    {activeScenario.description && (
                      <motion.p
                        className="mt-1 text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary line-clamp-2"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.3 }}
                      >
                        {activeScenario.description}
                      </motion.p>
                    )}
                    {activeScenario.tags && (
                      <motion.div
                        className="flex flex-wrap gap-1 mt-2"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.24, duration: 0.3 }}
                      >
                        {activeScenario.tags.split(',').map((tag, i) => (
                          <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium dark:bg-white/10 bg-black/5 dark:text-claude-darkTextSecondary text-claude-textSecondary">
                            {tag.trim()}
                          </span>
                        ))}
                      </motion.div>
                    )}
                    {activeScenario.skills && activeScenario.skills.length > 0 && (
                      <motion.div
                        className="flex flex-wrap gap-1 mt-2"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.28, duration: 0.3 }}
                      >
                        <span className="text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary mr-0.5">{i18nService.t('scenariosLinkedSkills')}</span>
                        {activeScenario.skills.map((skill) => (
                          <span key={skill.skill_id} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] dark:bg-white/5 bg-black/[0.03] dark:text-claude-darkTextSecondary text-claude-textSecondary">
                            {skill.display_name || skill.skill_name}
                          </span>
                        ))}
                      </motion.div>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Greeting 气泡 */}
              {greeting && (
                <motion.div
                  className="mx-auto max-w-lg rounded-2xl dark:bg-claude-darkSurface bg-white border dark:border-claude-darkBorder border-claude-border px-4 py-3 shadow-sm"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.32, duration: 0.3 }}
                >
                  <p className="text-sm dark:text-claude-darkText text-claude-text whitespace-pre-wrap leading-relaxed">
                    {greeting}
                  </p>
                </motion.div>
              )}

              {/* Trigger Prompts 快捷提示 */}
              {triggerPrompts.length > 0 && (
                <motion.div
                  className="mx-auto max-w-lg grid grid-cols-1 sm:grid-cols-2 gap-2"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.38, duration: 0.3 }}
                >
                  {triggerPrompts.map((p, i) => {
                    const label = p.label || p.prompt;
                    const fullPrompt = p.prompt || p.label;
                    if (!label) return null;
                    return (
                      <button
                        key={i}
                        type="button"
                        title={fullPrompt}
                        onClick={() => fillPromptIntoInput(fullPrompt)}
                        className="group flex items-center gap-2 text-left rounded-xl border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-white hover:border-claude-accent/50 hover:dark:border-claude-accent/40 px-3 py-2.5 text-[13px] dark:text-claude-darkText text-claude-text transition-colors shadow-sm"
                      >
                        <span className="flex-1 truncate">{label}</span>
                        <ChevronRightIcon className="h-3.5 w-3.5 shrink-0 dark:text-claude-darkTextSecondary text-claude-textSecondary group-hover:text-claude-accent transition-colors" />
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </div>
            );
          })()}


          {/* 场景模式保留已连接工具；默认首页按参考布局保持留白。 */}
          {activeScenario && (
            <ConnectedToolsBar
              onTaskSelect={(task) => {
                promptInputRef.current?.setValue(task);
                promptInputRef.current?.focus();
              }}
              onSkillsActivate={(skillIds) => {
                if (skillIds.length > 0) {
                  dispatch(setActiveSkillIds(skillIds));
                } else {
                  dispatch(clearActiveSkills());
                }
              }}
            />
          )}
        </div>
      </div>

      {/* 默认首页输入框固定在页面底部。 */}
      {!activeScenario && (
        <motion.div
          className="shrink-0 px-4 pb-6 pt-2"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12, duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="mx-auto w-full min-w-0 max-w-3xl">
            {promptInputElement}
          </div>
        </motion.div>
      )}

      {/* Prompt Input Area — 场景模式贴底(场景横幅内容长,输入框固定可见) */}
      {activeScenario && (
        <motion.div
          className="shrink-0 px-4 pb-6 pt-2"
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.32, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="mx-auto w-full min-w-0 max-w-3xl">
            {promptInputElement}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default CoworkView;
